import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { fetchExperiences } from "store/experiences"
import { clearedCalendar, getAvailabilityCalendar } from "store/availability"
import { buildMonthGrid } from "../utils"

/**
 * Owns the availability page's state: which listing, which month, and the day feed for the two.
 *
 * The listing is required before anything is fetched (A1) — the page shows its empty state until
 * one is picked, which is the design's first frame.
 */
export const useAvailabilityCalendar = () => {
  const dispatch = useDispatch()

  const [listingId, setListingId] = useState(null)
  const [monthDate, setMonthDate] = useState(() => moment().startOf("month"))

  const listings = useSelector((state) => state.experiences?.list || [])
  const isLoadingListings = useSelector((state) => Boolean(state.experiences?.loading))
  const days = useSelector((state) => state.availability?.calendar?.days || {})
  const listing = useSelector((state) => state.availability?.calendar?.listing || null)
  const isLoading = useSelector((state) => Boolean(state.availability?.calendar?.loading))
  const hasFailed = useSelector((state) => Boolean(state.availability?.calendar?.failed))

  const { weeks, start, end } = useMemo(() => buildMonthGrid(monthDate), [monthDate])

  useEffect(() => {
    dispatch(fetchExperiences())
  }, [dispatch])

  useEffect(() => {
    if (!listingId) {
      dispatch(clearedCalendar())
      return
    }
    // `api()` rejects on failure and the reducer's `failed` flag is what the grid reads.
    dispatch(getAvailabilityCalendar({ listing_id: listingId, start_date: start, end_date: end })).catch(() => {})
  }, [dispatch, listingId, start, end])

  const listingOptions = useMemo(
    () => listings.map((experience) => ({ value: String(experience.id), label: experience.title || experience.name })),
    [listings]
  )

  const stepMonth = useCallback((offset) => setMonthDate((current) => current.clone().add(offset, "month")), [])

  return {
    listingId,
    setListingId,
    listingOptions,
    isLoadingListings,
    monthDate,
    stepMonth,
    weeks,
    days,
    /** The listing as the availability feed describes it — carries `listing_type` for ticketed. */
    listing,
    isLoading,
    hasFailed
  }
}

export default useAvailabilityCalendar
